'use client';

import { Dialog as SheetPrimitive } from '@base-ui/react/dialog';
import { cn } from '@zealot/ui/lib/utils';
import { X } from 'lucide-react';

type SheetSide = 'top' | 'right' | 'bottom' | 'left';

const SHEET_SIZE_CLASSES = {
  horizontal: {
    sm: 'sm:max-w-sm',
    default: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    full: 'sm:max-w-2xl',
  },
  vertical: {
    sm: 'max-h-[40vh]',
    default: 'max-h-[50vh]',
    lg: 'max-h-[60vh]',
    xl: 'max-h-[70vh]',
    full: 'max-h-[85vh]',
  },
} as const;

type SheetSize = keyof (typeof SHEET_SIZE_CLASSES)['horizontal'];

const getSheetSizeClass = (side: SheetSide, size: SheetSize) => {
  const axis = side === 'left' || side === 'right' ? 'horizontal' : 'vertical';
  return SHEET_SIZE_CLASSES[axis][size];
};

const Sheet = SheetPrimitive.Root;

const SheetTrigger = ({ className, ...props }: SheetPrimitive.Trigger.Props) => {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" className={className} {...props} />;
};

const SheetClose = (props: SheetPrimitive.Close.Props) => {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
};

const SheetPortal = (props: SheetPrimitive.Portal.Props) => {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
};

const SheetOverlay = ({ className, ...props }: SheetPrimitive.Backdrop.Props) => {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0',
        className,
      )}
      {...props}
    />
  );
};

const sheetPopupBaseClasses = [
  'bg-background text-foreground border-border fixed z-50 flex flex-col gap-4 shadow-lg',
  'transition-[translate,opacity] duration-200 ease-out',
  'data-starting-style:opacity-0 data-ending-style:opacity-0',
  'data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l',
  'data-[side=right]:data-starting-style:translate-x-full data-[side=right]:data-ending-style:translate-x-full',
  'data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r',
  'data-[side=left]:data-starting-style:-translate-x-full data-[side=left]:data-ending-style:-translate-x-full',
  'data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:border-b',
  'data-[side=top]:data-starting-style:-translate-y-full data-[side=top]:data-ending-style:-translate-y-full',
  'data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:border-t',
  'data-[side=bottom]:data-starting-style:translate-y-full data-[side=bottom]:data-ending-style:translate-y-full',
].join(' ');

type SheetPopupProps = SheetPrimitive.Popup.Props & {
  side?: SheetSide;
  size?: SheetSize;
};

const SheetPopup = ({ className, side = 'right', size = 'default', ...props }: SheetPopupProps) => {
  return (
    <SheetPrimitive.Popup
      data-slot="sheet-popup"
      data-side={side}
      className={cn(sheetPopupBaseClasses, getSheetSizeClass(side, size), className)}
      {...props}
    />
  );
};

const SheetHeader = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return <div data-slot="sheet-header" className={cn('flex flex-col gap-1.5 p-6 pb-0', className)} {...props} />;
};

const SheetFooter = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="sheet-footer"
      className={cn('mt-auto flex flex-col-reverse gap-2 p-6 pt-0 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
};

const SheetTitle = ({ className, ...props }: SheetPrimitive.Title.Props) => {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('font-semibold text-lg leading-none tracking-tight', className)}
      {...props}
    />
  );
};

const SheetDescription = ({ className, ...props }: SheetPrimitive.Description.Props) => {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
};

type SheetContentProps = SheetPrimitive.Popup.Props & {
  side?: SheetSide;
  size?: SheetSize;
  hideClose?: boolean;
};

const SheetContent = ({
  className,
  side = 'right',
  size = 'default',
  hideClose = false,
  children,
  ...props
}: SheetContentProps) => {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPopup className={className} side={side} size={size} {...props}>
        {children}
        {!hideClose && (
          <SheetPrimitive.Close
            data-slot="sheet-close-button"
            className="absolute top-4 right-4 rounded-md text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none"
          >
            <X className="size-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPopup>
    </SheetPortal>
  );
};

export type { SheetContentProps, SheetPopupProps, SheetSide, SheetSize };
export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPopup,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
